import React from 'react';
import { Search, X } from 'lucide-react';

interface FiltersPanelProps {
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  selectedActivityType: string;
  setSelectedActivityType: (val: string) => void;
  selectedDistanceRange: string;
  setSelectedDistanceRange: (val: string) => void;
  selectedDateRange: string;
  setSelectedDateRange: (val: string) => void;
  sortByField: string;
  setSortByField: (val: string) => void;
  sortOrderDirection: 'ASC' | 'DESC';
  setSortOrderDirection: (val: 'ASC' | 'DESC') => void;
  getActivityEmoji: (type: string) => string;
}

export const FiltersPanel: React.FC<FiltersPanelProps> = ({
  searchQuery,
  setSearchQuery,
  selectedActivityType,
  setSelectedActivityType,
  selectedDistanceRange,
  setSelectedDistanceRange,
  selectedDateRange,
  setSelectedDateRange,
  sortByField,
  setSortByField,
  sortOrderDirection,
  setSortOrderDirection,
  getActivityEmoji,
}) => {
  const isFilterActive =
    searchQuery ||
    selectedActivityType !== 'all' ||
    selectedDistanceRange !== 'all' ||
    selectedDateRange !== 'all' ||
    sortByField !== 'startTime' ||
    sortOrderDirection !== 'DESC';

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedActivityType('all');
    setSelectedDistanceRange('all');
    setSelectedDateRange('all');
    setSortByField('startTime');
    setSortOrderDirection('DESC');
  };

  return (
    <div
      className="glass-panel"
      style={{
        padding: '20px',
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.06)',
        marginBottom: '24px',
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      {/* Row 1: Search & Type */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 300px' }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-secondary)',
              opacity: 0.6,
            }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by activity name..."
            style={{
              width: '100%',
              background: 'rgba(0, 0, 0, 0.25)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '30px',
              color: '#fff',
              padding: '12px 16px 12px 42px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s ease',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['all', 'running', 'cycling', 'hiking', 'climbing'].map((type) => (
            <button
              key={type}
              onClick={() => setSelectedActivityType(type)}
              style={{
                padding: '10px 18px',
                borderRadius: '30px',
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'capitalize',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: selectedActivityType === type ? 'var(--accent-gradient)' : 'rgba(255,255,255,0.03)',
                border: '1px solid ' + (selectedActivityType === type ? 'transparent' : 'rgba(255,255,255,0.06)'),
                color: '#fff',
              }}
            >
              {type === 'all' ? 'All Activities' : getActivityEmoji(type) + ' ' + type}
            </button>
          ))}
        </div>
      </div>

      {/* Row 2: Selectors */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          borderTop: '1px solid rgba(255,255,255,0.04)',
          paddingTop: '16px',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Distance
          </label>
          <select
            value={selectedDistanceRange}
            onChange={(e) => setSelectedDistanceRange(e.target.value)}
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.08)',
              padding: '8px 12px',
              borderRadius: '8px',
              outline: 'none',
              fontSize: '13px',
              width: '150px',
            }}
          >
            <option value="all">Any distance</option>
            <option value="&lt;10">&lt; 10 km</option>
            <option value="10-25">10 - 25 km</option>
            <option value="25-50">25 - 50 km</option>
            <option value="50-100">50 - 100 km</option>
            <option value="100+">100+ km</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Time Range
          </label>
          <select
            value={selectedDateRange}
            onChange={(e) => setSelectedDateRange(e.target.value)}
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.08)',
              padding: '8px 12px',
              borderRadius: '8px',
              outline: 'none',
              fontSize: '13px',
              width: '150px',
            }}
          >
            <option value="all">All time</option>
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="year">This year</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Sort By
          </label>
          <div style={{ display: 'flex', gap: '6px' }}>
            <select
              value={sortByField}
              onChange={(e) => setSortByField(e.target.value)}
              style={{
                background: 'rgba(0, 0, 0, 0.3)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.08)',
                padding: '8px 12px',
                borderRadius: '8px',
                outline: 'none',
                fontSize: '13px',
                width: '160px',
              }}
            >
              <option value="startTime">Activity Date</option>
              <option value="totalDistance">Distance</option>
              <option value="totalDuration">Duration</option>
              <option value="createdAt">Upload Date</option>
            </select>
            <button
              onClick={() => setSortOrderDirection(sortOrderDirection === 'ASC' ? 'DESC' : 'ASC')}
              style={{
                background: 'rgba(255,255,255,0.03)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.08)',
                padding: '8px 12px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              {sortOrderDirection === 'ASC' ? '▲ Asc' : '▼ Desc'}
            </button>
          </div>
        </div>

        {/* Reset Filters */}
        {isFilterActive && (
          <button
            onClick={handleClearFilters}
            style={{
              background: 'none',
              border: 'none',
              color: '#ff385c',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              alignSelf: 'flex-end',
              paddingBottom: '8px',
            }}
          >
            <X size={14} />
            <span>Clear Filters</span>
          </button>
        )}
      </div>
    </div>
  );
};
